import {
  AdminPanelSettings,
  CloseOutlined,
  EmailOutlined,
  Person,
} from "@mui/icons-material";
import PasswordIcon from "@mui/icons-material/Password";
import PhoneIcon from "@mui/icons-material/Phone";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import {
  Datagrid,
  downloadCSV,
  List,
  RaRecord,
  TextField,
  useListContext,
  useRecordContext,
  useRefresh,
} from "react-admin";
import sendRequest from "../dataProvider/sendRequest";
import CustomButtonCancel from "../unit/CustomButtonCancel";
import CustomButtonSubmit from "../unit/CustomButtonSubmit";
import CustomDateField from "../unit/CustomDateField";
import CustomEditIcon from "../unit/CustomEditIcon";
import CustomEditRemoveIcon from "../unit/CustomEditRemoveIcon";
import CustomLoading from "../unit/CustomLoading";
import formatDate from "../unit/formatDate";
import HmSubHeaderTitle from "../unit/HmSubHeaderTitle";
import PostPagination from "../unit/PostPagination";
import SwitchIcon from "../unit/svgIcon/SwitchIcon";
import SwitchOnIcon from "../unit/svgIcon/SwitchOnIcon";
import useNotify from "../unit/useNotify";
import BankerEdit from "./BankerEdit";
import BankerListActions from "./BankerListActions";
import { getBankerListFilters } from "./BankerListFilters";
import BankListAside from "./BankListAside";
import { hmLightTheme } from "../themes/hmTheme";
import VendorsAdd from "../vendors/VendorsAdd";
import BankerAdd from "./BankerAdd";
import HmLoading from "../unit/HmLoading";
import NoResults from "../unit/NoResults";
import NoPermission from "../unit/NoPermission";
import { triggerClientErrorForTest } from "../utils/clientErrorLogger";

const bankerTypeExporter = (records: any) => {
  let data = [];
  let title = {};
  data = records.map((record: any) => {
    const { bankadmin, id, name, email, mobilephone, sStatus, created_at } =
      record;
    return {
      bankadmin: bankadmin == 1 ? "Bank Admin" : "",
      id: id,
      name: name,
      email: email,
      mobilephone: mobilephone,
      created_at: formatDate(created_at),
      status: sStatus == "IN-USE" ? "Active" : "Inactive",
    };
  });
  title = {
    bankadmin: "Role",
    id: "Member ID",
    name: "Account Name",
    email: "Account Email",
    mobilephone: "Mobile",
    created_at: "Created Time",
    status: "Status",
  };
  return {
    title: title,
    data: data,
  };
};

const bankerExporter = (records: any) => {
  const filterData = bankerTypeExporter(records);

  let csvTitle = filterData.title;
  let data = filterData.data;
  const csvData = [{ ...csvTitle }, ...data];
  const csvDataStr = csvData.map((row) => Object.values(row).join(","));
  downloadCSV(csvDataStr.join("\n"), "Member List");
};

const CustomBankerAdmin = ({ ...props }) => {
  const record = useRecordContext();
  if (!record) return null;
  if (record?.bankadmin) {
    return (
      <Person sx={{ marginRight: "5px", color: "#1976d2", fontSize: "22px" }} />
    );
  }
};
const CustomBankerNameField = ({ ...props }) => {
  let record = useRecordContext();
  if (!record) return null;

  return <Typography>{record?.name}</Typography>;
};

const BankerList = () => {
  const authrole = localStorage.getItem("authrole");
  const permissions =
    localStorage.getItem("bbaccess")?.toString().split(",") || [];
  if (!permissions.includes("banker_admin")) {
    return <NoPermission />;
  }
  const refresh = useRefresh();
  const [editRecord, setEditRecord] = React.useState<RaRecord | null>(null);
  const [open, setOpen] = React.useState(false);
  const { successNotify, errorNotify, infoNotify } = useNotify();
  const [record, setRecord] = React.useState<{ total: number } | null>(null);
  const [currentTab, setCurrentTab] = React.useState("info");

  const handleClose = () => {
    setOpen(false);
    setEditRecord(null);
    refresh();
  };

  const handleCancel = () => {
    setEditRecord(null);
  };

  const handleButtonClick = (record: any) => {
    setOpen(true);
    setEditRecord(record);
  };

  const CustomButton = ({ onClick }) => {
    const record = useRecordContext();
    if (!record) return null;
    const handleClick = (showTab) => {
      onClick(record);
      setCurrentTab(showTab);
    };

    const handleChangeStatus = () => {
      let newStatus = record.sStatus == "IN-USE" ? "DISABLE" : "IN-USE";

      var reqUrl = "bankers/actions/changeStatus";
      var response = sendRequest(reqUrl, {
        id: record.id,
        data: {
          status: newStatus,
        },
      });
      response
        .then((res) => {
          if (res.code == 200) {
            successNotify("Status changed successfully");
            refresh();
          } else {
            errorNotify("Failed to change status");
          }
        })
        .catch((error) => {
          errorNotify("Error changing status: " + error.message);
        });
    };
    const handleRemove = () => {
      var reqUrl = `bankers/actions/delete`;
      sendRequest(reqUrl, { id: record?.id }).then((response) => {
        if (response && response.code == "200") {
          successNotify("Data deleted successfully");
          handleClose();
        } else {
          errorNotify("Data not deleted");
        }
      });
      setOpen(false);
      setEditRecord(null);
    };

    const PermissionIcon = ({ ...props }) => {
      const auth = localStorage.getItem("authrole");
      let disabled = true;
      if (auth == "admin" || auth == "bankadmin") {
        disabled = false;
      } else {
        return null;
      }
      return (
        <Tooltip title={props?.label || "Permission"} placement="left" arrow>
          <IconButton disabled={disabled}>
            <AdminPanelSettings
              sx={{
                width: "22px",
                height: "22px",
                cursor: "pointer",
                color: disabled ? "grey" : "#1976d2",
              }}
              onClick={props.onClick}
            />
          </IconButton>
        </Tooltip>
      );
    };
    return (
      <>
        <CustomEditIcon
          onClick={() => handleClick("info")}
          label="Profile Edit"
        />
        <PermissionIcon
          onClick={() => handleClick("permission")}
          label="Access Permission"
        />
        <Tooltip title="Alert Emails" placement="left" arrow>
          <IconButton
            size="small"
            onClick={() => handleClick("useralert")}
            sx={{ marginLeft: "5px", p: 1, color: "#1976d2" }}
          >
            <EmailOutlined />
          </IconButton>
        </Tooltip>

        {/* change password */}

        <Tooltip title="Password Reset" placement="left" arrow>
          <IconButton
            size="small"
            onClick={() => handleClick("changePwd")}
            sx={{ marginLeft: "5px", p: 1, color: "#1976d2" }}
          >
            <PasswordIcon />
          </IconButton>
        </Tooltip>

        {localStorage.getItem("authrole") == "admin" && (
          <CustomEditRemoveIcon
            onClick={() => handleRemove()}
            message='Are you sure you want to permanently remove this member? Click "Confirm" to proceed.'
          />
        )}

        <Tooltip
          title={
            `` + (record?.sStatus == "IN-USE" ? "Deactivate" : "Activate") + ``
          }
          placement="left"
        >
          <IconButton
            size="small"
            onClick={handleChangeStatus}
            sx={{ marginLeft: "5px", p: 1 }}
          >
            {record?.sStatus == "IN-USE" ? (
              <SwitchOnIcon color="red" />
            ) : (
              <SwitchIcon color="green" />
            )}
          </IconButton>
        </Tooltip>
      </>
    );
  };
  const CustomRowStyle = (record: any) => {
    if (!record) return {};

    if (record?.sStatus == "IN-USE") {
      return {
        backgroundColor: "#fff",
        "&.MuiTableRow-hover:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      };
    } else {
      return {
        backgroundColor: "#f5f5f5",
        "& td.MuiTableCell-root": {
          color: "gray",
        },
        "& td.MuiTableCell-root span": {
          color: "gray",
        },
        "& td.MuiTableCell-root a": {
          color: "gray",
        },
        "& td.MuiTableCell-root p": {
          color: "gray",
        },
      };
    }
  };
  const CustomBankerList = () => {
    const listContext = useListContext();
    const { total, data, isPending, isLoading, isFetching } = listContext;
    return (
      <Datagrid
        optimized
        rowClick={false}
        empty={false}
        size="small"
        id="table-bankers"
        rowSx={CustomRowStyle}
        className="hm-list-datagrid"
        sx={{
          ".MuiTableCell-root": {
            padding: "6px",
          },
          ".RaDatagrid-headerCell:first-child": {
            borderTopLeftRadius: "0px",
          },
          ".RaDatagrid-headerCell:last-child": {
            borderTopRightRadius: "0px",
          },
          "& .column-groups": {
            md: { display: "none" },
            lg: { display: "table-cell" },
          },
          "& .Mui-active": {
            color: "gray",
          },
          "& .MuiTableSortLabel-root": {
            padding: "5px",
          },
          "& .RaDatagrid-headerCell": {
            color: "gray",
            fontSize: "1rem",
            backgroundColor: "#edf4fb",
            wordWrap: "break-word",
          },
          "& .MuiTableCell-paddingCheckbox": {
            display: "none",
          },
          "& .RaDatagrid-headerCell.column-bankadmin": {
            minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
            width: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
            maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdSmallWidth,
          },
          "& .RaDatagrid-headerCell.column-id": {
            minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
            width: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
            maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
          },
          "& .RaDatagrid-headerCell.column-mobilephone": {
            minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
            width: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
            maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdMiddleWidth,
          },
          "& .RaDatagrid-headerCell.column-action": {
            minWidth: "280px",
            width: "280px",
            maxWidth: "280px",
          },
          "& .RaDatagrid-headerCell.column-name": {
            minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
            width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
            maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
            wordBreak: "break-word",
            overflowX: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
          },
          "& .RaDatagrid-headerCell.column-email": {
            minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
            width: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
            maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdTextWidth,
            wordBreak: "break-word",
            overflowX: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
          },
          "& .RaDatagrid-headerCell.column-created_at": {
            minWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
            width: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
            maxWidth: hmLightTheme?.palette?.otherSpacing?.tableTdDateWidth,
          },
          "& .MuiTableCell-root.column-action": {
            textAlign: "center",
          },
        }}
      >
        <CustomBankerAdmin source="bankadmin" label="Role" />
        <TextField source="id" label="Member ID" cellClassName="numericText" />
        {authrole == "admin" && <TextField source="sBankName" label="Bank" />}
        <CustomBankerNameField source="name" label="Account Name" />
        <TextField source="email" label="Account Email" />
        <TextField
          source="mobilephone"
          label="Mobile"
          cellClassName="numericText"
        />
        <CustomDateField
          source="created_at"
          label="Created Time"
          onlyShowDate={true}
          cellClassName="dateText"
        />
        <CustomButton
          label="Action"
          source="action"
          onClick={handleButtonClick}
          sortable={false}
        />
      </Datagrid>
    );
  };
  let popHeight = "480px";
  if (currentTab == "permission") {
    popHeight = "480px";
  } else if (currentTab == "changePwd") {
    popHeight = "350px";
  } else if (currentTab == "contact") {
    popHeight = "400px";
  } else if (currentTab == "info") {
    if (authrole == "admin") {
      popHeight = "570px";
    } else {
      popHeight = "510px";
    }
  }
  return (
    <>
      <List
        resource="bankers"
        actions={<BankerListActions />}
        filters={getBankerListFilters()}
        aside={<BankListAside selected="bankers" />}
        exporter={bankerExporter}
        sx={{
          width: "100%",
          borderRadius: "0px",
          "& .MuiCard-root": {
            borderRadius: "0px",
          },
          "& .RaList-actions form": {
            flex: "0 1 350%",
          },
          "& .RaList-actions form .filter-field .MuiInputBase-input": {
            width: "230px",
          },
          "& .RaList-actions .RaFilterForm-filterFormInput:first-child .MuiInputBase-root::before":
            {
              display: "none",
            },
          "& .RaList-actions .filter-field:first-child .MuiFormControl-root": {
            marginLeft: 0,
          },
        }}
        pagination={<PostPagination />}
        perPage={25}
        empty={false}
        emptyWhileLoading={false}
        loading={<HmLoading />}
      >
        <CustomBankerList />
        <CustomLoading />
      </List>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        // maxWidth="md"
        maxWidth={currentTab == "changePwd" ? "sm" : "md"}
        //currentTab
        sx={
          {
            // "& .MuiDialog-paper": {
            //   minHeight: popHeight,
            // },
          }
        }
        PaperProps={{
          component: "form",
          onSubmit: async (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            let form = document.querySelector(".banker_edit_dialog_form form");
            let formData = new FormData(form as HTMLFormElement);
            let formJson = Object.fromEntries(formData.entries());

            interface SaveFormJson {
              [key: string]: string | string[] | undefined;
              bank?: string;
              sFname?: string;
              sLname?: string;
              email?: string;
              mobilephone?: string;
              password?: string;
              sStatus?: string;
              bankadmin?: string;
              bbPermission?: string;
              saveType?: string;
            }

            let saveFormJson: SaveFormJson = {};

            if (currentTab == "info") {
              // saveFormJson.bank = formJson?.bank as string | undefined;
              saveFormJson.sFname = formJson?.sFname as string | undefined;
              saveFormJson.sLname = formJson?.sLname as string | undefined;
              saveFormJson.email = formJson?.email as string | undefined;
              saveFormJson.sStatus = formJson?.sStatus as string | undefined;
              saveFormJson.bankadmin = formJson?.bankadmin as
                | string
                | undefined;
              saveFormJson.mobilephone = formJson?.mobilephone as
                | string
                | undefined;
            } else if (currentTab == "changePwd") {
              const passwordRuleText =
                "Minimum 8 characters with at least 1 letter, 1 number, and 1 special character";
              const passwordPattern =
                /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9\s]).{8,}$/;
              const newPassword = formJson?.newPassword as string | undefined;
              const confirmPassword = formJson?.confirmPassword as
                | string
                | undefined;

              if (!newPassword) {
                errorNotify("Password is required");
                return;
              }

              if (!passwordPattern.test(newPassword)) {
                errorNotify(passwordRuleText);
                return;
              }

              saveFormJson.password = formJson?.newPassword as
                | string
                | undefined;
            } else if (currentTab == "useralert") {
              const alertFields = [
                "sReceiveAppSubmitMail",
                "sReceiveAppFinalReviewMail",
                "sReceiveAppNotificationMail",
                "sReceiveAppAcceptedCounteredMail",
                "sReceiveAppAcceptedPreApprovalMail",
                "sReceiveAppCompletedSignMail",
                "sReceiveAppBecomeActiveLoanMail",
                "sReceiveAppTemplatesChangedMail",
                "sReceiveAppValidatedMail",
                // "sReceiveAccountSubmitMail",
                // "sReceiveAccountNotificationMail",
                // "sReceiveAccountCompletedSignMail",
                // "sReceiveAccountTemplatesChangedMail",
                "sReceive_event_pmt_loan_mod_requested",
                "sReceive_event_application_external_signing_completed",
                "sReceive_event_application_switch_ach_complete",
                "sReceive_event_application_switch_ach",
                "sReceive_event_pmt_customer_transfer_failed",
                "sReceive_event_pmt_attention_failed_transfer",
              ];

              const userAlertInputs = document.querySelectorAll(
                ".banker_edit_dialog_form .useralert input[type='checkbox']"
              );
              const selectedEvents = new Set<string>();
              userAlertInputs.forEach((input) => {
                const checkbox = input as HTMLInputElement;
                if (checkbox.checked) {
                  selectedEvents.add(checkbox.value);
                }
              });

              alertFields.forEach((field) => {
                saveFormJson[field] = selectedEvents.has(field)
                  ? [String(editRecord?.id)]
                  : [];
              });
            } else {
              let permission = "";
              const permissionInputs = document.querySelectorAll(
                ".banker_edit_dialog_form .permission input"
              );
              permissionInputs.forEach((input) => {
                if ((input as HTMLInputElement).checked) {
                  permission += (input as HTMLInputElement).name + ",";
                }
              });

              saveFormJson.bbPermission = permission;
            }

            formJson.saveType = currentTab;
            saveFormJson.saveType =
              currentTab == "useralert" ? "user_alert" : currentTab;

            const reqUrl = `bankers/actions/edit`;
            const response = await sendRequest(reqUrl, {
              id: editRecord?.id,
              data: saveFormJson,
            });
            if (response && response.code == "200") {
              successNotify("Data saved successfully");
              handleClose();
            } else {
              errorNotify(response?.message || "Data not saved!");
            }
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            mb: 1,
            borderBottom: "1px solid #e5e5e5",
            color: "#465384",
            textTransform: "uppercase",
          }}
        >
          {/* Member Edit Record #{editRecord?.id} */}
          {currentTab == "info"
            ? "Member Edit"
            : currentTab == "permission"
            ? "Access Permitted"
            : currentTab == "changePwd"
            ? "Change Password"
            : currentTab == "contact"
            ? "Change Contact"
            : currentTab == "useralert"
            ? "User Alert"
            : ""}
          <IconButton
            style={{ position: "absolute", right: "10px", top: "10px" }}
            onClick={handleClose}
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {editRecord && (
            <BankerEdit
              id={editRecord.id}
              basePath="/bankers"
              resource="bankers"
              curTab={currentTab}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <CustomButtonCancel onClick={handleClose} />
          {/* <CustomButtonRemove onClick={handleRemove} /> */}
          <CustomButtonSubmit />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BankerList;
